import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import useAuthStore from "../store/auth.store";
import { isPlayerAccount } from "../components/common/RequireNotPlayer";
import useWalletStore from "../store/wallet.store";
import GlobalNav from "../components/layout/GlobalNav";
import WalletModalsHost from "../components/wallet/WalletModalsHost";
import TrialBanner from "../components/wallet/TrialBanner";
import FeedbackWidget from "../components/feedback/FeedbackWidget";
import ErrorBoundary from "../components/common/ErrorBoundary";
import { ToastHost } from "../components/common/ToastHost";
import KeyboardHelp from "../components/common/KeyboardHelp";
import VersionWatcher from "../components/common/VersionWatcher";
import { Loader2 } from "lucide-react";

// 2026-05-30 — Player-account allowlist. These are the only paths a
// "player" persona (no org) may visit; everything else redirects to
// /me/player. Backend permissions are the real enforcement.
const PLAYER_ALLOWED_PATHS = [
  "/me/player",
  "/account/player-profile",
  "/account/privacy",
];

export default function AccountLayout() {
  const { isAuthenticated, loading, user } = useAuthStore();
  const { pathname } = useLocation();
  const initWalletEventListeners = useWalletStore((s) => s.initWalletEventListeners);
  const fetchBalance = useWalletStore((s) => s.fetchBalance);

  useEffect(() => {
    if (isAuthenticated) {
      initWalletEventListeners();
      fetchBalance();
    }
  }, [isAuthenticated, initWalletEventListeners, fetchBalance]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-main">
        <div className="flex items-center gap-2 text-text-secondary text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading…</span>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Pre-launch lockout — STANDBY accounts see the maintenance page
  // instead of the dashboard. Flipped per-user via /admin/auth/user/.
  if (user?.status === "STANDBY" && pathname !== "/maintenance") {
    return <Navigate to="/maintenance" replace />;
  }

  // Player-only accounts are locked to the player hub + profile editor.
  if (isPlayerAccount(user)) {
    const allowed = PLAYER_ALLOWED_PATHS.some(
      (p) => pathname === p || pathname.startsWith(p + "/")
    );
    if (!allowed) return <Navigate to="/me/player" replace />;
  }

  return (
    <ToastHost>
      <div className="min-h-screen bg-bg-main">
        <GlobalNav />
        <TrialBanner />
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
        <WalletModalsHost />
        <FeedbackWidget />
        <KeyboardHelp />
        <VersionWatcher />
      </div>
    </ToastHost>
  );
}
